import { pack } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CreateObject } from './create-object'
import { CreateTuple } from './create-tuple'
import { CommonName, UnsupportedNodeError } from './utils'

/**
 * TODO (object -> { key: string; value: any }[])
 *
 * @param todo TODO
 * @example
 * TODO
 * // ->
 * TODO
 */
export interface ObjectToList<out T = any> {
  // TODO: maybe use more specific list type for ObjectToList and ListToObject (e.g. { key: string; value: any }[])
  /** {@link ObjectToList} */
  readonly [Key.ObjectToList]: T extends readonly unknown[]
    ?
        | Expression<Object.Constraint>
        | readonly [
            objectExpression: Expression<Object.Constraint>,
            keyProp?: string,
            valueProp?: string,
          ]
    : never
}
export namespace ObjectToList {
  export const isObjectToList: TypeChecker<ObjectToList> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<ObjectToList>({
        [Key.ObjectToList]: TypeChecker.isAny, // TODO:
      }),
    ).usingAlias('Fn.ObjectToList')
  export const assertIsObjectToList: TypeAsserter<ObjectToList> =
    TypeAsserter.Factory.from(isObjectToList)

  /**
   * @see {@link Node}
   */
  export class Node<
    T extends readonly unknown[] = readonly unknown[],
  > extends fnNode.Node<
    T,
    Key.ObjectToList,
    {
      readonly object: fnNode.Node<Object.Constraint>
      /**
       * Entries in the resulting list should have a property where this is the
       * key and the value is the __key__ for that entry
       */
      readonly keyProp: string
      /**
       * Entries in the resulting list should have a property where this is the
       * key and the value is the __value__ for that entry
       */
      readonly valueProp: string
    }
  > {
    public readonly type = common.value.types.Intrinsic.List

    constructor(arg: Node<T>['arg']) {
      super(Key.ObjectToList, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<ObjectToList> = {
    createNode: (fn, context) => {
      const [
        objectExpression,
        keyProp = CommonName.Key,
        valueProp = CommonName.Value,
      ] = pack(fn[Key.ObjectToList])
      const object = expressionNode.from(objectExpression, context)
      if (expressionNode.ForValue.isForValue(object)) {
        if (TypeChecker.isNullish(object.value)) {
          // If the object is nullish, we will just carry that through to the
          // result
          return object
        }
        // If object is a non-nullish value, we can convert to list and return the resulting
        // value
        return new expressionNode.ForValue(
          // NOTE: the coalescing won't happen at runtime, but is required to
          // let TypeScript know that object.value will never be nullish
          Object.entries(object.value ?? {}).map(([key, value]) => ({
            [keyProp]: key,
            [valueProp]: value,
          })),
        )
      }
      if (object instanceof CreateObject.Node) {
        // If the object is a CreateObject expression, we can go ahead and
        // convert it to a list of expressions
        return new CreateTuple.Node(
          Object.entries(object.arg).map(([key, value]) =>
            expressionNode.ForValue.isForValue(value)
              ? // Each case where the value is known, we can make the whole entry a
                // value node. Minor note: we can't make the whole list a value
                // node since that would have been handled in the previous if
                // block
                new expressionNode.ForValue({
                  [keyProp]: key,
                  [valueProp]: value.value,
                })
              : // Where the value is not known, we can use CreateObject
                new CreateObject.Node({
                  [keyProp]: new expressionNode.ForValue(key),
                  [valueProp]: value,
                }),
          ),
        )
      }
      if (object instanceof fnNode.Node) {
        return new Node({ keyProp, object, valueProp })
      }
      // If we get to this point, we're not sure what else to do
      throw new UnsupportedNodeError()
    },
    expressionsInArgs: fn => {
      const arg = fn[Key.ObjectToList]
      return [TypeChecker.isReadonlyArray(arg) ? arg[0] : arg]
    },
    typeChecker: isObjectToList,
  }
}
